// FontAwesome -> Cygnus icon-name map for the Yorlin/Musubi FA removal sweep
// (icarus-core 3.1.x dropped FontAwesome for the Cygnus sprite set).
//
// Keys are bare FA names (no `fa-`/`fal`/`fas` prefix). Values are Cygnus
// base names that exact-match the installed manifest
// (@apexl/icarus-core/src/assets/icons/manifest.mjs, 101 glyphs). Every value
// here MUST resolve non-null through resolveIconName — enforced by
// scripts/checkIconResolves.mjs.
//
// Where a name has no exact Cygnus glyph it maps to the semantic-nearest of
// the 101 (Toby-approved: map to existing glyphs now, bespoke glyphs later).
// See the migration report for the bespoke-glyph wishlist.

export const NAME_MAP = {
  // ── arrows / chevrons ─────────────────────────────────────────────────
  'align-left': 'list',
  'angle-right': 'chevron-right',
  'angle-down': 'chevron-down',
  'angle-left': 'chevron-left',
  'angle-up': 'chevron-up',
  'arrow-down': 'chevron-down',
  'arrow-down-wide-short': 'chevron-down',
  'arrow-up': 'chevron-up',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'arrow-pointer': 'hand',
  'arrow-right-arrow-left': 'shuffle',
  'arrow-rotate-left': 'refresh',
  'arrow-up-right-from-square': 'external-link',
  'arrow-right-from-bracket': 'sign-out',
  'arrows-rotate': 'refresh',
  'caret-down': 'chevron-down',
  'caret-right': 'chevron-right',
  'caret-up': 'chevron-up',
  'caret-left': 'chevron-left',
  'chevron-down': 'chevron-down',
  'chevron-left': 'chevron-left',
  'chevron-right': 'chevron-right',
  'chevron-up': 'chevron-up',

  // ── status / marks ────────────────────────────────────────────────────
  'ban': 'x-circle',
  'check': 'check',
  'check-circle': 'check-circle',
  'check-double': 'check',
  'circle': 'checkbox',        // radio-unselected (see manual notes)
  'circle-check': 'check-circle',
  'circle-dashed': 'minus',
  'circle-dot': 'checkbox',    // radio-selected (solid variant, manual)
  'circle-exclamation': 'warning',
  'circle-info': 'info-circle',
  'circle-minus': 'minus',
  'circle-notch': 'spinner',
  'circle-pause': 'pause',
  'circle-xmark': 'x-circle',
  'cross': 'cross',
  'exclamation-triangle': 'warning',
  'times': 'x',
  'times-circle': 'x-circle',
  'triangle-exclamation': 'warning',
  'xmark': 'x',

  // ── people ────────────────────────────────────────────────────────────
  'address-card': 'user',
  'user': 'user',
  'user-chart': 'chart-line',
  'user-check': 'user-check',
  'user-gear': 'user-gear',
  'user-plus': 'user-plus',
  'user-secret': 'user-shield',
  'user-shield': 'user-shield',
  'user-unlock': 'user-check',
  'users': 'users',
  'users-medical': 'users',
  'users-viewfinder': 'users',
  'fingerprint': 'shield',

  // ── comms / mail ──────────────────────────────────────────────────────
  'at': 'envelope',
  'bell': 'bell',
  'comment': 'note-sticky',
  'comment-dots': 'note-sticky',
  'comment-plus': 'note-sticky',
  'comments': 'note-sticky',
  'envelope': 'envelope',
  'envelope-open': 'envelope',
  'envelope-open-text': 'envelope-open-text',
  'envelopes-bulk': 'envelope',
  'inbox': 'envelope',
  'inbox-in': 'envelope',
  'paper-plane': 'envelope',
  'paper-plane-top': 'envelope',
  'phone': 'phone',
  'mobile': 'mobile',
  'mobile-retro': 'mobile',
  'fax': 'fax',
  'quote-left': 'note-sticky',
  'reply': 'arrow-left',
  'reply-all': 'arrow-left',

  // ── AI / energy (single consistent AI affordance = bolt) ──────────────
  'bolt': 'bolt',
  'bolt-slash': 'pause',
  'lightbulb-on': 'bolt',
  'lightbulb': 'bolt',
  'microchip-ai': 'bolt',
  'microchip': 'server',
  'robot': 'bolt',
  'sparkles': 'bolt',
  'wand-magic-sparkles': 'bolt',

  // ── charts / gauges / diagrams ────────────────────────────────────────
  'chart-line': 'chart-line',
  'chart-bar': 'chart-bar',
  'chart-mixed': 'chart-bar',
  'chart-pie': 'chart-bar',
  'chart-simple': 'chart-bar',
  'gauge': 'gauge',
  'gauge-high': 'gauge',
  'traffic-light': 'gauge',
  'diagram-project': 'diagram',
  'diagram': 'diagram',
  'sitemap': 'diagram',

  // ── lists / tables ────────────────────────────────────────────────────
  'list': 'list',
  'list-check': 'list',
  'list-timeline': 'stream',
  'list-ul': 'list',
  'clipboard-check': 'check-circle',
  'clipboard-list': 'list',
  'table': 'table',
  'table-columns': 'table',
  'table-list': 'table',
  'stream': 'stream',
  'layer-group': 'layer-group',

  // ── files / docs ──────────────────────────────────────────────────────
  'copy': 'file',
  'file': 'file',
  'file-arrow-down': 'download',
  'file-code': 'file-code',
  'file-lines': 'file',
  'file-pdf': 'file',
  'file-shield': 'file',
  'file-invoice-dollar': 'file-invoice-dollar',
  'floppy-disk': 'download',
  'image': 'file',
  'newspaper': 'newspaper',
  'note-sticky': 'note-sticky',
  'sticky-note': 'note-sticky',
  'print': 'file',
  'book': 'book',

  // ── security ──────────────────────────────────────────────────────────
  'key': 'lock',
  'lock': 'lock',
  'shield': 'shield',
  'shield-alt': 'shield',
  'shield-check': 'shield',
  'shield-halved': 'shield',
  'scale-balanced': 'shield',

  // ── nav / place ───────────────────────────────────────────────────────
  'building': 'building',
  'building-columns': 'building',
  'building-flag': 'building',
  'briefcase': 'building',
  'compass': 'route',
  'crosshairs': 'pin',
  'globe': 'globe',
  'home': 'home',
  'house': 'home',
  'location-dot': 'pin',
  'map-location-dot': 'pin',
  'map-pin': 'pin',
  'pin': 'pin',
  'route': 'route',
  'bullseye': 'pin',
  'bullseye-arrow': 'pin',
  'bullseye-pointer': 'pin',

  // ── tools / actions ───────────────────────────────────────────────────
  'box': 'box',
  'box-archive': 'box',
  'box-open': 'box',
  'box-up': 'box',
  'boxes-alt': 'packages',
  'boxes-stacked': 'packages',
  'packages': 'packages',
  'gear': 'gear',
  'gears': 'gear',
  'cog': 'gear',
  'cogs': 'gear',
  'sliders': 'gear',
  'toolbox': 'wrench',
  'tools': 'wrench',
  'wrench': 'wrench',
  'screwdriver-wrench': 'wrench',
  'hammer': 'hammer',
  'pen': 'pen',
  'pen-nib': 'pen',
  'pen-ruler': 'pen',
  'pen-to-square': 'pen',
  'pencil': 'pen',
  'edit': 'pen',
  'trash': 'trash',
  'download': 'download',
  'upload': 'upload',
  'filter': 'filter',
  'filter-circle-dollar': 'filter',
  'magnifying-glass': 'search',
  'magnifying-glass-location': 'search',
  'binoculars': 'search',
  'search': 'search',
  'eye': 'eye',
  'eye-slash': 'eye',
  'refresh': 'refresh',
  'recycle': 'refresh',
  'rotate': 'refresh',
  'rotate-left': 'refresh',
  'rotate-right': 'refresh',
  'clock-rotate-left': 'refresh',
  'sync': 'refresh',

  // ── media / broadcast ─────────────────────────────────────────────────
  'play': 'play',
  'pause': 'pause',
  'bullhorn': 'bullhorn',
  'broadcast': 'broadcast',
  'radar': 'broadcast',
  'satellite-dish': 'broadcast',
  'signal-stream': 'broadcast',
  'rocket': 'rocket',
  'rocket-launch': 'rocket',

  // ── misc glyphs ───────────────────────────────────────────────────────
  'flag': 'flag',
  'flag-checkered': 'flag',
  'palette': 'palette',
  'code': 'code',
  'code-branch': 'code',
  'code-compare': 'code',
  'plug': 'plug',
  'plus': 'plus',
  'minus': 'minus',
  'link': 'link',
  'external-link': 'external-link',
  'tag': 'tags',
  'tags': 'tags',
  'clock': 'clock',
  'calendar': 'calendar',
  'calendar-clock': 'calendar',
  'hourglass-half': 'clock',
  'heart': 'heart',
  'heart-pulse': 'heart',
  'fire': 'flame',
  'flame': 'flame',
  'water': 'droplet',
  'droplet': 'droplet',
  'seedling': 'droplet',
  'cloud': 'server',
  'cloud-check': 'check-circle',
  'cloud-error': 'cloud-error',
  'cloud-exclamation': 'cloud-error',
  'gem': 'award',
  'star': 'award',
  'trophy': 'award',
  'medal': 'medal',
  'award': 'award',
  'bug': 'warning',
  'window': 'window',
  'window-maximize': 'window',
  'window-minimize': 'minus',
  'browser': 'window',
  'desktop': 'window',
  'server': 'server',
  'database': 'database',
  'puzzle': 'puzzle',
  'puzzle-piece': 'puzzle',
  'shapes': 'shapes',
  'info-square': 'info-circle',
  'info-circle': 'info-circle',
  'info': 'info-circle',
  'cart-plus': 'basket-shopping',
  'basket-shopping': 'basket-shopping',
  'store': 'store',
  'credit-card': 'credit-card',
  'coins': 'coins',
  'boat': 'boat',
  'ship': 'boat',
  'sailboat': 'boat',
  'hand': 'hand',
  'hand-pointer': 'hand',
  'grip-vertical': 'more',
  'grip-dots-vertical': 'more',
  'ellipsis': 'more',
  'more': 'more',
  'question': 'question',
  'folder': 'folder',
  'barrier-block': 'barrier-block',
  'sign-out': 'sign-out',
};

export default NAME_MAP;
